import type { Skill } from '../domain.js';

/**
 * Логіка, просторове мислення й фінансова грамотність.
 *
 * Це навмисно окремий предмет, а не «домішка» до математики: у старому банку
 * «логіка» була переодягненою арифметикою (числові прогресії), і справжніх
 * задач на міркування там майже не було. Тут кожна навичка — окремий тип
 * мислення, і генератори мають робити саме його.
 */
export const LOGIC_SKILLS: Skill[] = [
  // ── Закономірності ──────────────────────────────────────────────────────
  {
    id: 'logic.patterns.number_sequences',
    subject: 'logic',
    strand: 'patterns',
    title: 'Числові послідовності',
    description: 'Знаходить правило ряду, зокрема нелінійне, і продовжує його.',
    gradeBand: [3, 6],
    prerequisites: [],
    baseDifficulty: 1000,
    generators: ['logic.numberSequence'],
  },
  {
    id: 'logic.patterns.visual',
    subject: 'logic',
    strand: 'patterns',
    title: 'Візуальні закономірності',
    description: 'Продовжує ряд фігур за формою, кольором, поворотом.',
    gradeBand: [3, 6],
    prerequisites: [],
    baseDifficulty: 1020,
    generators: ['logic.visualPattern'],
  },
  {
    id: 'logic.patterns.matrix',
    subject: 'logic',
    strand: 'patterns',
    title: 'Матриці 3×3',
    description: 'Добирає відсутній елемент матриці за подвійним правилом.',
    gradeBand: [4, 6],
    prerequisites: ['logic.patterns.visual'],
    baseDifficulty: 1210,
    generators: ['logic.matrix'],
  },

  // ── Міркування ──────────────────────────────────────────────────────────
  {
    id: 'logic.reasoning.odd_one_out',
    subject: 'logic',
    strand: 'reasoning',
    title: 'Що зайве',
    description: 'Знаходить елемент, який не належить до групи, і пояснює ознаку.',
    gradeBand: [3, 6],
    prerequisites: [],
    baseDifficulty: 950,
    generators: ['logic.oddOneOut'],
  },
  {
    id: 'logic.reasoning.analogies',
    subject: 'logic',
    strand: 'reasoning',
    title: 'Аналогії',
    description: 'Добудовує пару за тим самим відношенням: A до B як C до ?',
    gradeBand: [4, 6],
    prerequisites: ['logic.reasoning.odd_one_out'],
    baseDifficulty: 1100,
    generators: ['logic.analogy'],
  },
  {
    id: 'logic.reasoning.deduction',
    subject: 'logic',
    strand: 'reasoning',
    title: 'Логічні задачі',
    description: 'Виводить розв\'язок із кількох умов (хто де живе, хто що любить).',
    gradeBand: [4, 6],
    prerequisites: [],
    baseDifficulty: 1180,
    foundational: true,
    generators: ['logic.deductionGrid'],
  },
  {
    id: 'logic.reasoning.truth_lies',
    subject: 'logic',
    strand: 'reasoning',
    title: 'Правда і брехня',
    description: 'Визначає, хто говорить правду, за суперечливими твердженнями.',
    gradeBand: [5, 6],
    prerequisites: ['logic.reasoning.deduction'],
    baseDifficulty: 1280,
    generators: ['logic.truthLies'],
  },
  {
    id: 'logic.reasoning.statements',
    subject: 'logic',
    strand: 'reasoning',
    title: 'Істинність тверджень',
    description: 'Перевіряє «усі / деякі / жоден» і знаходить контрприклад.',
    gradeBand: [5, 6],
    prerequisites: ['logic.reasoning.deduction'],
    baseDifficulty: 1240,
    generators: ['logic.statements'],
  },

  // ── Простір ─────────────────────────────────────────────────────────────
  {
    id: 'logic.spatial.rotation',
    subject: 'logic',
    strand: 'spatial',
    title: 'Обертання фігур',
    description: 'Упізнає ту саму фігуру після повороту чи дзеркального відбиття.',
    gradeBand: [3, 6],
    prerequisites: [],
    baseDifficulty: 1060,
    generators: ['logic.rotation'],
  },
  {
    id: 'logic.spatial.nets_cubes',
    subject: 'logic',
    strand: 'spatial',
    title: 'Розгортки й куби',
    description: 'Співвідносить розгортку з об\'ємною фігурою, рахує куби в стосі.',
    gradeBand: [4, 6],
    prerequisites: ['logic.spatial.rotation'],
    baseDifficulty: 1190,
    generators: ['logic.netsCubes'],
  },

  // ── Комбінаторика і стратегії ───────────────────────────────────────────
  {
    id: 'logic.combinatorics.counting',
    subject: 'logic',
    strand: 'combinatorics',
    title: 'Перебір варіантів',
    description: 'Рахує кількість комбінацій систематичним перебором.',
    gradeBand: [4, 6],
    prerequisites: [],
    baseDifficulty: 1150,
    generators: ['logic.counting'],
  },
  {
    id: 'logic.puzzles.classic',
    subject: 'logic',
    strand: 'puzzles',
    title: 'Класичні головоломки',
    description: 'Зважування, переливання, переправа — задачі на стратегію.',
    gradeBand: [4, 6],
    prerequisites: ['logic.reasoning.deduction'],
    baseDifficulty: 1260,
    generators: ['logic.classicPuzzle'],
  },
  {
    id: 'logic.algorithms.sequencing',
    subject: 'logic',
    strand: 'algorithms',
    title: 'Алгоритмічне мислення',
    description: 'Складає й виконує послідовність команд, знаходить помилку в алгоритмі.',
    gradeBand: [3, 6],
    prerequisites: [],
    baseDifficulty: 1070,
    generators: ['logic.algorithm'],
  },

  // ── Фінансова грамотність ───────────────────────────────────────────────
  {
    id: 'logic.money.basics',
    subject: 'logic',
    strand: 'money',
    title: 'Гроші й покупки',
    description: 'Рахує вартість, решту, порівнює ціну за одиницю товару.',
    gradeBand: [3, 6],
    prerequisites: [],
    baseDifficulty: 990,
    generators: ['logic.moneyBasics'],
  },
  {
    id: 'logic.money.planning',
    subject: 'logic',
    strand: 'money',
    title: 'Заощадження й бюджет',
    description: 'Планує накопичення, оцінює знижки, розрізняє потребу й бажання.',
    gradeBand: [4, 6],
    prerequisites: ['logic.money.basics'],
    baseDifficulty: 1160,
    generators: ['logic.moneyPlanning'],
  },
];
